import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Users, UserCheck, Tag, Wallet, DollarSign, ChevronRight, Calendar } from 'lucide-react';
import { resolveEventEndDateTime } from '../../lib/eventDateTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact "Last Event Summary" card for the Host Dashboard.
 *
 * Strategy (additive — zero new backend endpoints or collections):
 *   1. Receives the host's full `events` list from Dashboard (already fetched).
 *   2. Picks the most-recent COMPLETED event — completion = effective end
 *      datetime (resolveEventEndDateTime) is strictly in the past.
 *   3. Fetches a one-event Sales Snapshot from the existing
 *      /api/reports/summary?event_ids=<id> endpoint and renders a compact
 *      7-metric card.
 *
 * Hidden entirely (returns null) when there is no completed event. Failures
 * are swallowed — the dashboard never breaks because of this card.
 */
export default function LastEventSummaryCard({ events = [] }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  // Pick the most-recent completed event from the host's event list.
  const lastCompletedEvent = React.useMemo(() => {
    const now = Date.now();
    const candidates = [];
    for (const e of events) {
      // Skip draft/archived — only consider events that actually happened.
      if (e.status !== 'active' && e.status !== 'completed') continue;
      const endDt = resolveEventEndDateTime({
        date: e.date,
        time: e.time,
        end_time: e.end_time,
        end_date: e.end_date,
        is_multi_day: !!e.is_multi_day,
      }) || (e.date && e.time ? new Date(`${e.date}T${e.time}`) : null);
      if (!endDt || Number.isNaN(endDt.getTime())) continue;
      if (endDt.getTime() >= now) continue; // still upcoming / in progress
      candidates.push({ ev: e, endMs: endDt.getTime() });
    }
    if (!candidates.length) return null;
    candidates.sort((a, b) => b.endMs - a.endMs);
    return candidates[0].ev;
  }, [events]);

  useEffect(() => {
    if (!lastCompletedEvent) return;
    let cancelled = false;
    setLoading(true);
    setErrored(false);
    const token = localStorage.getItem('session_token');
    axios
      .get(`${API}/reports/summary`, {
        params: { event_ids: lastCompletedEvent.event_id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (!cancelled) setSummary(res.data); })
      .catch(() => { if (!cancelled) setErrored(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lastCompletedEvent]);

  if (!lastCompletedEvent) return null;      // per spec: hide entirely
  if (errored) return null;                  // fail quietly
  if (loading || !summary) {
    return (
      <div className="mb-5 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-4" data-testid="last-event-summary-loading">
        <div className="h-4 w-40 bg-[#141414] animate-pulse rounded" />
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-14 bg-[#141414] animate-pulse rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  const kpis = summary.kpis || {};
  const topTier = (summary.ticket_type_breakdown || [])[0]?.tier_name || '—';
  const topMethod = (summary.payment_method_mix || [])[0]?.method || '—';
  const topMethodLabel = topMethod === 'unknown' ? 'Other' :
    topMethod.charAt(0).toUpperCase() + topMethod.slice(1);
  const grossTotal = typeof summary.reported_gross_total === 'number' ? summary.reported_gross_total : 0;

  const eventDateLabel = (() => {
    if (!lastCompletedEvent.date) return '';
    try {
      const d = new Date(`${lastCompletedEvent.date}T${lastCompletedEvent.time || '00:00'}`);
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return lastCompletedEvent.date; }
  })();

  return (
    <div
      className="mb-5 bg-gradient-to-br from-[#0A0A0A] to-[#050505] border border-[#D4AF37]/20 rounded-sm p-4 sm:p-5"
      data-testid="last-event-summary-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-semibold uppercase mb-1 flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3" />
            Last Event Summary
          </div>
          <div className="text-base sm:text-lg font-bold text-[#EAE7E1] truncate" data-testid="last-event-name">
            {lastCompletedEvent.title}
          </div>
          <div className="text-xs text-[#AFAFAF] flex items-center gap-1.5 mt-0.5" data-testid="last-event-date">
            <Calendar className="w-3 h-3" />
            {eventDateLabel}
          </div>
        </div>
        <button
          onClick={() => navigate(`/reports?event_ids=${encodeURIComponent(lastCompletedEvent.event_id)}`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/70 transition-colors flex-shrink-0"
          data-testid="last-event-view-report"
        >
          View Full Report
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="last-event-metrics">
        <Metric testId="metric-total-passes" icon={<Tag className="w-3.5 h-3.5" />} label="Total Passes" value={kpis.total_passes ?? 0} />
        <Metric testId="metric-total-attendees" icon={<Users className="w-3.5 h-3.5" />} label="Attendees" value={kpis.total_attendees ?? 0} />
        <Metric
          testId="metric-checked-in"
          icon={<UserCheck className="w-3.5 h-3.5" />}
          label="Checked-In"
          value={`${kpis.checked_in_percent ?? 0}%`}
          sublabel={`${kpis.checked_in_count ?? 0} / ${kpis.total_passes ?? 0}`}
        />
        <Metric testId="metric-reported-gross" icon={<DollarSign className="w-3.5 h-3.5" />} label="Reported Gross" value={`$${grossTotal.toFixed(2)}`} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <Pill testId="metric-top-tier" icon={<Tag className="w-3 h-3" />} label="Top Ticket Type" value={topTier} />
        <Pill testId="metric-top-method" icon={<Wallet className="w-3 h-3" />} label="Top Payment" value={topMethodLabel} />
      </div>
    </div>
  );
}

function Metric({ icon, label, value, sublabel, testId }) {
  return (
    <div className="bg-[#050505] border border-[#1A1A1A] rounded-sm p-3" data-testid={testId}>
      <div className="text-[10px] tracking-wider uppercase text-[#777] mb-1 flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className="text-lg sm:text-xl font-black text-[#EAE7E1] leading-tight">{value}</div>
      {sublabel && <div className="text-[10px] text-[#777] mt-0.5">{sublabel}</div>}
    </div>
  );
}

function Pill({ icon, label, value, testId }) {
  return (
    <div className="bg-[#050505] border border-[#1A1A1A] rounded-sm px-3 py-2 flex items-center gap-2 min-w-0" data-testid={testId}>
      <div className="text-[10px] tracking-wider uppercase text-[#777] flex items-center gap-1.5 flex-shrink-0">
        {icon}
        {label}
      </div>
      <div className="ml-auto text-sm font-semibold text-[#EAE7E1] truncate">{value}</div>
    </div>
  );
}
